import { useState } from 'react';
import { useQuery } from 'react-query';
import { DefaultTable } from 'Components/DefaultTable';
import { Container } from '../../Components/Container';
import { Information } from 'Components/Information';
import { tableHeader } from './table-header.js';
import { ModalConfirmDelete } from 'Components/ModalConfirmDelete';
import { deleteUser, enableUser, getUsers } from 'services/users-service';
import { PageLoaderList } from 'Components/PageLoader/List';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  FormControlLabel,
  Pagination,
  Stack,
} from '@mui/material';
import { NoData } from 'Components/NoData';
import { Column, Row } from 'Components/Grid';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { Controller, useForm } from 'react-hook-form';
import { CustomTextField } from 'Components/Form/styles';
import { Card } from 'Components/Card';
import { PageHelper } from '../../Components/PageHelper';

// Tour steps
const tourSteps = [
  {
    target: 'body',
    content: 'Bem-vindo à página de Usuários! Aqui você pode gerenciar todos os colaboradores e usuários com acesso ao sistema.',
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-tour="page-title"]',
    content: 'Este é o título da página. Clique aqui para retornar à página anterior.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="add-button"]',
    content: 'Clique neste botão para adicionar um novo usuário ao sistema.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="info-section"]',
    content: 'Esta seção descreve o propósito da página: gerenciar colaboradores e usuários do sistema.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="filters-card"]',
    content: 'Use este card de filtros para buscar usuários específicos por nome, email, grupo ou status ativo/inativo.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="active-checkbox"]',
    content: 'Marque ou desmarque esta opção para filtrar usuários ativos ou inativos.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="filter-fields"]',
    content: 'Preencha um ou mais campos para filtrar usuários por Nome, Email ou Grupo.',
  },
  {
    target: '[data-tour="filter-buttons"]',
    content: 'Use "Limpar" para resetar os filtros ou "Filtrar" para aplicar a busca.',
  },
  {
    target: '[data-tour="users-table"]',
    content: 'Esta tabela exibe todos os usuários cadastrados. Você pode visualizar detalhes, editar, ativar/desativar ou excluir usuários.',
  },
  {
    target: '[data-tour="pagination"]',
    content: 'Use a paginação para navegar entre as páginas de usuários quando houver muitos registros.',
  },
  {
    target: 'body',
    content: 'Tour concluído! Agora você pode gerenciar os usuários do sistema com facilidade.',
    placement: 'center',
  },
];

export const Users = () => {
  const { t } = useTranslation(['user', 'common']);
  const selectedTenant = useSelector(useAuthTenant);
  const SwalReact = withReactContent(Swal);
  const [open, setOpen] = useState(false);
  const [refetch, setRefetch] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [page, setPage] = useState(1);

  const { register, control, reset, getValues } = useForm({
    defaultValues: {
      name: '',
      email: '',
      group: '',
      active: true,
    },
  });

  const getFilterPayload = () => {
    const name = getValues('name');
    const email = getValues('email');
    const group = getValues('group');

    if (name) return { typeFilter: 'Nome', searchContent: name };
    if (email) return { typeFilter: 'Email', searchContent: email };
    if (group) return { typeFilter: 'Grupo', searchContent: group };
    return { typeFilter: '', searchContent: '' };
  };

  const {
    isLoading,
    error,
    data: pagedUsers,
  } = useQuery(['getUsers', refetch, page], async () => {
    const { typeFilter, searchContent } = getFilterPayload();
    const result = await getUsers(
      selectedTenant.slug,
      page,
      15,
      typeFilter,
      searchContent,
      getValues('active')
    );
    if (result?.data.data.length > 0) {
      return result?.data;
    }
    return {
      data: [],
      page,
      totalPages: 0,
    };
  });

  const onDelete = item => {
    setItemToDelete(item);
    setOpen(true);
  };

  const onClose = () => {
    setItemToDelete(null);
    setOpen(false);
  };

  const onConfirm = async () => {
    try {
      const result = await deleteUser(itemToDelete?._id);
      if (result.status === 204) {
        onClose();
        setRefetch(!refetch);
        SwalReact.fire({
          title: t('user:alert.successTitle'),
          text: t('user:users.success.disabled'),
          icon: 'success',
        });
      } else {
        throw new Error('Ocorreu um erro ao tentar inativar o Usuário!');
      }
    } catch (error) {
      SwalReact.fire({
        title: 'Erro!',
        text: error.response.data.message ?? error.message,
        icon: 'error',
      });
    }
  };

  const onChangeStatus = async item => {
    try {
      const result = await enableUser(item?._id);
      if (result.status === 200) {
        onClose();
        setRefetch(!refetch);
      } else {
        throw new Error('Ocorreu um erro ao tentar habilitar o Usuário!');
      }
    } catch (error) {
      SwalReact.fire({
        title: 'Erro!',
        text: error.response.data.message ?? error.message,
        icon: 'error',
      });
    }
  };

  return (
    <>
      {/* Helper Interativo */}
      <PageHelper 
        steps={tourSteps}
        tourKey="users-tour"
        autoStart={true}
        showButton={true}
      />
      
      <Container title={t('user:users.title')} breadcrumb={t('user:users.breadcrumb')} showButtonAdd linkPage="/users">
      <div data-tour="info-section">
        <Information
          title={t('user:users.title')}
          description={t('user:users.description')}
          style={{
            marginTop: '24px',
          }}
        />
      </div>
      {error && <div>{t('user:users.error')}</div>}
      {!error && (
        <>
          <Card className="mb-2 mt-3" data-tour="filters-card">
            <Row className="mb-2" md={'3'}>
              <Column>
                <div data-tour="active-checkbox">
                  <Controller
                    control={control}
                    name="active"
                    render={({ field: { onChange, value } }) => (
                    <FormControlLabel
                      value={value}
                      control={
                        <Checkbox 
                          checked={!!value} 
                          onChange={(e) => {
                            onChange(e.target.checked);
                          }} 
                          name="active" 
                        />
                      }
                      label={t('user:users.table.active')}
                      sx={{ marginLeft: 0, marginBottom: '16px' }}
                    />
                  )}
                />
                </div>
              </Column>
            </Row>
            <Row data-tour="filter-fields">
              <Column xs={12} md={4}>
                <CustomTextField
                  {...register('name')}
                  label={t('user:users.filter.name')}
                  sx={{
                    minHeight: '40px',
                    maxHeight: '40px',
                    margin: '0 auto',
                    width: '100%',
                    marginBottom: '16px',
                    ' > div': {
                      minHeight: '40px',
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Column>
              <Column xs={12} md={4}>
                <CustomTextField
                  {...register('email')}
                  label={t('user:users.filter.email')}
                  sx={{
                    minHeight: '40px',
                    maxHeight: '40px',
                    margin: '0 auto',
                    width: '100%',
                    marginBottom: '16px',
                    ' > div': {
                      minHeight: '40px',
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Column>
              <Column xs={12} md={4}>
                <CustomTextField
                  {...register('group')}
                  label={t('user:users.filter.group')}
                  sx={{
                    minHeight: '40px',
                    maxHeight: '40px',
                    margin: '0 auto',
                    width: '100%',
                    marginBottom: '16px',
                    ' > div': {
                      minHeight: '40px',
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Column>
              <Column xs={12} md={'auto'}>
                <Stack direction="row" spacing={1} sx={{ marginTop: '16px' }} data-tour="filter-buttons">
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{
                      width: 'fit-content!important',
                      borderRadius: '12px',
                      padding: '10px 24px 10px 24px',
                      textTransform: 'none',
                    }}
                    onClick={() => {
                      reset();
                      setRefetch(!refetch);
                    }}
                  >
                    {t('common:buttons.clear')}
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    sx={{
                      width: 'fit-content!important',
                      borderRadius: '12px',
                      padding: '10px 24px 10px 24px',
                      textTransform: 'none',
                    }}
                    endIcon={<SearchOutlinedIcon />}
                    onClick={() => {
                      setRefetch(!refetch);
                    }}
                  >
                    {t('common:buttons.filter')}
                  </Button>
                </Stack>
              </Column>
            </Row>
          </Card>
          {isLoading && <PageLoaderList></PageLoaderList>}
          {!isLoading && pagedUsers.data.length === 0 && <NoData description={t('user:users.noData')} />}
          {!isLoading && pagedUsers.data?.length > 0 && (
            <div className="container mt-3">
              <div data-tour="users-table">
                <DefaultTable
                  rows={tableHeader(t)}
                  cells={pagedUsers.data}
                  onClickDelete={onDelete}
                  onClickStatus={onChangeStatus}
                  link="users"
                />
              </div>
              <Stack sx={{ justifyContent: 'center', flexDirection: 'row', marginTop: '16px' }} data-tour="pagination">
                <Pagination
                  count={pagedUsers.totalPages}
                  onChange={(_, selectedPage) => {
                    setPage(selectedPage);
                  }}
                  color="primary"
                  page={page}
                  showFirstButton
                  showLastButton
                />
              </Stack>
              <ModalConfirmDelete
                description={t('user:users.delete.confirm', { name: itemToDelete?.name })}
                open={open}
                onClose={onClose}
                onConfirm={onConfirm}
              />
            </div>
          )}
        </>
      )}
    </Container>
    </>
  );
};
